/** Gym cover photos go through the Worker media pipeline; legacy local data is discarded. */
import {
  memberPhotoMime,
  memberPhotoUploadsAvailable,
  type PhotoUploadProgressHandler,
  uploadMemberPhoto,
} from './memberMediaUpload';
import { coalesceImageDelivery } from './imageDelivery';
import { readGymCoverCrop, type GymCoverCrop } from './gymCoverCrop';

const PREFIX = 'ggb.gym-cover.';
const MAX_INPUT_BYTES = 40 * 1024 * 1024;
export const MAX_COVER_URL_LENGTH = 1_200_000;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
export interface CoverStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  key(index: number): string | null;
  readonly length: number;
}
const listeners = new Set<() => void>();
function publish() {
  for (const listener of listeners) listener();
}
export function subscribeGymCover(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
export function gymCoverKey(userId: string) {
  if (!/^[A-Za-z0-9_-]{1,80}$/.test(userId)) throw new Error('That gym image could not be saved.');
  return PREFIX + userId;
}
export function isGymCoverDataUrl(value: string) {
  return (
    value.length <= MAX_COVER_URL_LENGTH &&
    /^data:image\/jpeg;base64,[A-Za-z0-9+/]+={0,2}$/.test(value)
  );
}
/** Kept for older callers; browser-owned image data is never a display source. */
export function cssImageValue(_dataUrl: string): null {
  return null;
}
export function gymCoverFileError(file: { type: string; size: number }) {
  const mime = memberPhotoMime(file.type);
  if (mime && !ACCEPTED_TYPES.includes(mime)) return 'Use a JPEG, PNG, or WebP image.';
  if (file.size <= 0) return 'Choose an image under 8 MB.';
  if (file.size > MAX_INPUT_BYTES) return 'That camera photo is too large. Try a smaller picture.';
  return null;
}
function browserStore(): CoverStore {
  return localStorage;
}
/** Discard a legacy device-only image instead of presenting it as a shared upload. */
export function readGymCover(userId: string, store: CoverStore = browserStore()): null {
  try {
    store.removeItem(gymCoverKey(userId));
  } catch {}
  return null;
}
/** @deprecated Local data-URL covers are no longer written. */
export function writeGymCover(_userId: string, _dataUrl: string, _store?: CoverStore): void {
  throw new Error('Use uploadGymCover with a signed-in session.');
}
export function clearGymCover(userId: string, store: CoverStore = browserStore()) {
  store.removeItem(gymCoverKey(userId));
  publish();
}
export function clearGymCovers(store?: CoverStore) {
  try {
    const target = store ?? browserStore();
    const keys: string[] = [];
    for (let index = 0; index < target.length; index += 1) {
      const key = target.key(index);
      if (key?.startsWith(PREFIX)) keys.push(key);
    }
    for (const key of keys) target.removeItem(key);
  } catch {
    // Browsers may deny storage access; shared images do not depend on it.
  }
  publish();
}
type Fetcher = typeof fetch;
async function checkedJson(response: Response) {
  const body = await response.json().catch(() => null);
  if (!response.ok || !body?.data)
    throw new Error(body?.error?.message ?? 'The gym photo could not be saved.');
  return body.data;
}

export async function gymCoverUploadsAvailable(apiBase: string, fetcher: Fetcher = fetch) {
  return memberPhotoUploadsAvailable(apiBase, fetcher);
}

export async function ownGymId(apiBase: string, token: string, fetcher: Fetcher = fetch) {
  const data = await checkedJson(
    await fetcher(`${apiBase}/gyms/me`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  );
  return data.gymId as string;
}

export async function uploadGymCover(
  apiBase: string,
  token: string,
  blob: Blob,
  framing: GymCoverCrop,
  fetcher: Fetcher = fetch,
  onProgress?: PhotoUploadProgressHandler,
  checkpoint: { mediaId?: string } = {},
) {
  if (!token) throw new Error('Sign in to upload a gym cover.');
  if (!checkpoint.mediaId) {
    const gymId = await ownGymId(apiBase, token, fetcher);
    const { mediaId } = await uploadMemberPhoto(
      apiBase,
      token,
      { blob, targetType: 'gym', targetId: gymId, label: 'Uploading gym photo', onProgress },
      fetcher,
    );
    // If selecting the cover fails after upload, retry with the same photo.
    checkpoint.mediaId = mediaId;
  }
  return selectGymCover(apiBase, token, checkpoint.mediaId, framing, fetcher);
}

/** Feature a gym gallery photo by reference and save its framing separately from the pixels. */
export async function selectGymCover(
  apiBase: string,
  token: string,
  mediaId: string,
  framing: GymCoverCrop,
  fetcher: Fetcher = fetch,
) {
  if (!token) throw new Error('Sign in to choose a gym cover.');
  await checkedJson(
    await fetcher(`${apiBase}/gyms/me/cover`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ mediaId, mobileFraming: framing }),
    }),
  );
  publish();
  return mediaId;
}

export async function updateGymCoverFraming(
  apiBase: string,
  token: string,
  mediaId: string,
  framing: GymCoverCrop,
  fetcher: Fetcher = fetch,
) {
  if (!token) throw new Error('Sign in to adjust your gym cover.');
  await checkedJson(
    await fetcher(`${apiBase}/gyms/me/cover/framing`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ mediaId, framing }),
    }),
  );
  publish();
}

export async function removeGymCover(apiBase: string, token: string, fetcher: Fetcher = fetch) {
  if (!token) throw new Error('Sign in to remove your gym cover.');
  await checkedJson(
    await fetcher(`${apiBase}/gyms/me/cover`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }),
  );
  publish();
}

/** Shared cover delivery URL for a member's gym; 404 means the default hero is used. */
export function gymCoverImageUrl(apiBase: string, userId: string) {
  return `${apiBase}/profiles/${encodeURIComponent(userId)}/gym-cover?framing=mobile`;
}

export type GymCoverPhoto = {
  url: string;
  mediaId?: string;
  framing?: GymCoverCrop;
};

export type RemoteGymCover = GymCoverPhoto & {
  userId: string;
  token: string | undefined;
  revision: number;
};

/** Never show a previous profile or viewer's private cover while a new read is pending. */
export function resolveRemoteGymCover(input: {
  live: boolean;
  userId?: string;
  token?: string;
  revision: number;
  remote: RemoteGymCover | null;
}): RemoteGymCover | null {
  const remote = input.remote;
  return input.live &&
    remote?.userId === input.userId &&
    remote?.token === input.token &&
    remote?.revision === input.revision
    ? remote
    : null;
}

export async function loadGymCover(
  apiBase: string,
  userId: string,
  token: string | undefined,
  signal: AbortSignal,
  fetcher: Fetcher = fetch,
  deliveryVersion = '',
): Promise<(Omit<GymCoverPhoto, 'url'> & { blob: Blob }) | null> {
  if (signal.aborted || !/^[a-f\d]{8}-(?:[a-f\d]{4}-){3}[a-f\d]{12}$/i.test(userId)) return null;
  return coalesceImageDelivery(
    fetcher,
    JSON.stringify([apiBase, 'gym-cover', userId, token ?? null, deliveryVersion]),
    signal,
    async (sharedSignal) => {
      const response = await fetcher(gymCoverImageUrl(apiBase, userId), {
        signal: sharedSignal,
        cache: 'no-store',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (
        sharedSignal.aborted ||
        !response.ok ||
        !/^image\/(jpeg|png|webp)(?:;|$)/i.test(response.headers.get('Content-Type') ?? '')
      )
        return null;
      const blob = await response.blob();
      if (sharedSignal.aborted || !blob.size) return null;
      return {
        blob,
        mediaId: response.headers.get('X-GGB-Cover-Media-ID') ?? undefined,
        framing: readGymCoverCrop(response.headers.get('X-GGB-Mobile-Cover-Framing')),
      };
    },
  );
}
